import React, { useState, Suspense, lazy } from "react";
import {
  Box,
  Chip,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { PolicyOrAccount } from "../../interfaces/DTO/mislaka_xml_models";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { formatAdjustedProductType } from "../../utils/enumFormatter";
import { formatCurrency, formatHandlerCode } from "../../utils/namesFormater";
import { colorHandlerCode } from "../../utils/colorEnumFormatter";

const InsuranceProductDrawer = lazy(() => import("../PersonFinancialData/Insurance/InsuranceProductDrawer"));
const EditInsuranceProductDrawer = lazy(() => import("./EditInsuranceProductDrawer"));

export interface InsuranceProductOverride {
  policyNumber: string;
  primaryInsurance: number | null;
  secondaryInsurance: number | null;
  monthlyCost: number | null;
}

interface Props {
  handlerCode: string;
  rows: MimshakData[];
  originalRows?: MimshakData[];
  contacts?: Contact[];
  contactColorMap?: Record<number, string>;
  overrides?: Record<string, InsuranceProductOverride>;
  onOverrideChange?: (policyNumber: string, primaryInsurance: number | null, secondaryInsurance: number | null, monthlyCost: number | null) => void;
  onDeleteRow?: (policyNumber: string) => void;
  pendingEditPolicy?: string | null;
  onPendingEditConsumed?: () => void;
}

function findPolicyOrAccount(row: MimshakData): PolicyOrAccount | undefined {
  const products = row.data?.products ?? [];
  for (const product of products) {
    for (const pol of product.policies_or_accounts ?? []) {
      if (pol.policy_number === row.policy_number) return pol;
    }
  }
  return undefined;
}

export function getInsuranceTotalFee(row: MimshakData): number {
  const pol = findPolicyOrAccount(row);
  if (!pol) return row.deposit_amount ?? row.funds_sum ?? 0;

  const insuranceFee = (pol.insurence_coverages ?? [])
    .map((c: any) => c.coverage_details?.insurance_fees?.fee || 0)
    .reduce((sum: number, val: number) => sum + val, 0);

  const pensionFees = (pol.insurence_coverages ?? [])
    .flatMap((c: any) => c.coverages || [])
    .reduce(
      (sum: number, cov: any) =>
        sum +
        (cov.disability_coverage_cost || 0) +
        (cov.disabled_survivor_pension_cost || 0) +
        (cov.survivors_coverage_cost || 0),
      0
    );

  const total = insuranceFee + pensionFees;
  return total === 0 ? (row.deposit_amount ?? row.funds_sum ?? 0) : total;
}

export interface BasicInsuranceSplit {
  primary: number;
  secondary: number;
}

export function getBasicInsuranceSplit(row: MimshakData): BasicInsuranceSplit {
  const pol = findPolicyOrAccount(row);
  if (!pol?.insurence_coverages) return { primary: 0, secondary: 0 };

  const clientId = row.data?.products?.[0]?.client?.client_id;
  let primary = 0;
  let secondary = 0;

  pol.insurence_coverages.forEach((coverage: any) => {
    if (!coverage.basic_insurance?.length) return;
    const allInsuredDetails = coverage.insured_details || [];
    const isPrimaryInsured =
      allInsuredDetails.length === 0 ||
      allInsuredDetails.some((d: any) => !d.id_number || d.id_number === clientId);
    coverage.basic_insurance.forEach((b: any) => {
      const amount = b.insurance_amount || 0;
      if (isPrimaryInsured) primary += amount;
      else secondary += amount;
    });
  });

  return { primary, secondary };
}

function renderBasicInsurance(row: MimshakData, ov: InsuranceProductOverride | undefined, originalSplit?: BasicInsuranceSplit): React.ReactNode {
  const { primary: origPrimary, secondary: origSecondary } = originalSplit ?? getBasicInsuranceSplit(row);

  const displayPrimary = ov?.primaryInsurance ?? origPrimary;
  const displaySecondary = ov?.secondaryInsurance ?? origSecondary;

  if (displayPrimary === 0 && ov == null) return "-";

  const isPrimaryOverridden = ov?.primaryInsurance != null && ov.primaryInsurance !== origPrimary;
  const isSecondaryOverridden = ov?.secondaryInsurance != null && ov.secondaryInsurance !== origSecondary;
  const hasSecondary = origSecondary > 0 || (ov?.secondaryInsurance ?? 0) > 0;

  return (
    <Stack direction="column" spacing={0}>
      {hasSecondary && (
        <Typography variant="caption" sx={{ color: "gray", fontSize: "0.75rem" }}>ראשי</Typography>
      )}
      {isPrimaryOverridden && (
        <Typography variant="caption" sx={{ color: "text.disabled", textDecoration: "line-through", fontSize: "0.75rem" }}>
          {formatCurrency(origPrimary)}
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {formatCurrency(displayPrimary)}
      </Typography>

      {hasSecondary && (
        <>
          <Typography variant="caption" sx={{ color: "gray", fontSize: "0.75rem", mt: 0.5 }}>משני</Typography>
          {isSecondaryOverridden && (
            <Typography variant="caption" sx={{ color: "text.disabled", textDecoration: "line-through", fontSize: "0.75rem" }}>
              {formatCurrency(origSecondary)}
            </Typography>
          )}
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatCurrency(displaySecondary)}
          </Typography>
        </>
      )}
    </Stack>
  );
}

const InsuranceFeesByProduct: React.FC<Props> = ({
  handlerCode,
  rows,
  originalRows = [],
  contacts = [],
  contactColorMap = {},
  overrides = {},
  onOverrideChange,
  onDeleteRow,
  pendingEditPolicy,
  onPendingEditConsumed,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<MimshakData | null>(null);
  const [editingProduct, setEditingProduct] = useState<MimshakData | null>(null);

  // Auto-open edit drawer when a row was just dragged into this handler
  React.useEffect(() => {
    if (!pendingEditPolicy) return;
    const row = rows.find((r) => r.policy_number === pendingEditPolicy);
    if (!row) return;
    setEditingProduct(row);
    onPendingEditConsumed?.();
  }, [pendingEditPolicy, rows]);

  return (
    <Box mb={4}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Box width={10} height={22} borderRadius={2} bgcolor={colorHandlerCode(handlerCode)} />
          <Typography variant="h6">&nbsp; {formatHandlerCode(handlerCode)}</Typography>
        </Stack>
      </Stack>

      <Box
        p={1}
        borderTop={`6px solid ${colorHandlerCode(handlerCode)}`}
        borderRadius={1}
        sx={{
          background: `linear-gradient(to bottom, ${colorHandlerCode(handlerCode)}11 0%, #ffffff 60%)`,
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="right">סוג מוצר</TableCell>
              <TableCell align="right">פוליסה</TableCell>
              <TableCell align="right">כיסוי ביטוח בסיסי</TableCell>
              <TableCell align="right">עלות חודשית</TableCell>
            </TableRow>
          </TableHead>
          <Droppable droppableId={handlerCode}>
            {(droppableProvided, droppableSnapshot) => (
              <TableBody
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                sx={{ backgroundColor: droppableSnapshot.isDraggingOver ? "action.hover" : undefined }}
              >
                {rows.length === 0 && !droppableSnapshot.isDraggingOver && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "text.disabled" }}>
                      אין מוצרים
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, index) => {
                  const ov = overrides[row.policy_number];
                  const originalRow = originalRows.find((r) => r.policy_number === row.policy_number) ?? row;
                  const originalCost = getInsuranceTotalFee(originalRow);
                  const originalSplit = getBasicInsuranceSplit(originalRow);
                  const monthlyCost = ov?.monthlyCost ?? getInsuranceTotalFee(row);
                  const currentCost = ov?.monthlyCost ?? getInsuranceTotalFee(row);
                  const isCostOverridden = currentCost !== originalCost;

                  return (
                    <Draggable key={row.policy_number} draggableId={row.policy_number} index={index}>
                      {(draggableProvided) => (
                        <TableRow
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          <TableCell align="right" onClick={() => !row.policy_number?.startsWith("generated-") && setSelectedProduct(row)} sx={{ cursor: row.policy_number?.startsWith("generated-") ? "default" : "pointer" }}>
                            <Box position="relative">
                              {contactColorMap[row.contact_id] && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -8,
                                    right: -8,
                                    width: 0,
                                    height: 0,
                                    borderStyle: "solid",
                                    borderWidth: "0 8px 8px 0",
                                    borderColor: `transparent ${contactColorMap[row.contact_id]} transparent transparent`,
                                  }}
                                />
                              )}
                              <Typography variant="body2">
                                {row.adjusted_product_type ? formatAdjustedProductType(row.adjusted_product_type) : ""}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" onClick={() => !row.policy_number?.startsWith("generated-") && setSelectedProduct(row)} sx={{ cursor: row.policy_number?.startsWith("generated-") ? "default" : "pointer" }}>
                            {row.policy_number?.startsWith("generated-")
                              ? <Chip label="חדש" color="error" size="small" />
                              : <Typography variant="body2">{row.policy_number}</Typography>
                            }
                          </TableCell>
                          <TableCell align="right" onClick={() => onOverrideChange && setEditingProduct(row)} sx={{ cursor: onOverrideChange ? "pointer" : "default" }}>
                            {renderBasicInsurance(row, ov, originalSplit)}
                          </TableCell>
                          <TableCell align="right" onClick={() => onOverrideChange && setEditingProduct(row)} sx={{ cursor: onOverrideChange ? "pointer" : "default" }}>
                            <Stack direction="column" spacing={0} alignItems="flex-end">
                              {isCostOverridden && (
                                <Typography variant="caption" sx={{ color: "text.disabled", textDecoration: "line-through", fontSize: "0.75rem" }}>
                                  {formatCurrency(originalCost)}
                                </Typography>
                              )}
                              <Typography variant="body2">{formatCurrency(monthlyCost)}</Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </Box>

      <Suspense fallback={null}>
        {selectedProduct?.data?.products?.[0] && (
          <InsuranceProductDrawer
            product={selectedProduct.data.products[0]}
            onClose={() => setSelectedProduct(null)}
          />
        )}
      </Suspense>

      <Suspense fallback={null}>
        {editingProduct && onOverrideChange && (
          <EditInsuranceProductDrawer
            open={!!editingProduct}
            mimshak={editingProduct}
            contact={contacts.find((c) => c.id === editingProduct.contact_id)}
            originalSplit={getBasicInsuranceSplit(editingProduct)}
            originalMonthlyCost={getInsuranceTotalFee(editingProduct)}
            currentPrimaryInsurance={overrides[editingProduct.policy_number]?.primaryInsurance ?? null}
            currentSecondaryInsurance={overrides[editingProduct.policy_number]?.secondaryInsurance ?? null}
            currentMonthlyCost={overrides[editingProduct.policy_number]?.monthlyCost ?? null}
            onUpdate={(policyNumber, primaryInsurance, secondaryInsurance, monthlyCost) => {
              console.log(`[InsuranceFeesByProduct.onUpdate] policy=${policyNumber} primary=${primaryInsurance} secondary=${secondaryInsurance} cost=${monthlyCost}`);
              const coverages = editingProduct.data?.products?.[0]?.policies_or_accounts?.[0]?.insurence_coverages ?? [];
              console.log(`[InsuranceFeesByProduct.onUpdate] coverages before:`, coverages.map((c: any, i: number) => ({
                idx: i, kind: c.coverage_details?.insurance_kind, insurance_sum: c.coverage_details?.insurance_sum, basic_insurance: c.basic_insurance,
              })));
              onOverrideChange(policyNumber, primaryInsurance, secondaryInsurance, monthlyCost);
            }}
            onClose={() => setEditingProduct(null)}
            onDelete={onDeleteRow && editingProduct.policy_number?.startsWith("generated-") ? () => {
              onDeleteRow(editingProduct.policy_number);
              setEditingProduct(null);
            } : undefined}
          />
        )}
      </Suspense>
    </Box>
  );
};

export default InsuranceFeesByProduct;
